import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthProvider';
import { useI18n } from '@/i18n/LanguageContext';
import { supabase } from '@/lib/supabase';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

interface RemovalNotification {
  id: string;
  car_id: string;
  removed_by: string;
  car_make: string | null;
  car_model: string | null;
  car_license_plate: string | null;
  remover_display_name: string | null;
}

const CarRemovalNotification: React.FC = () => {
  const { user } = useAuth();
  const { t } = useI18n();
  const [notification, setNotification] = useState<RemovalNotification | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkForNotifications = async () => {
      if (!user?.id) {
        setLoading(false);
        return;
      }

      try {
        // Fetch undismissed removal notifications
        const { data: notifications, error } = await supabase
          .from('car_removal_notifications')
          .select('id, car_id, removed_by, car_make, car_model, car_license_plate')
          .eq('user_id', user.id)
          .is('dismissed_at', null)
          .order('created_at', { ascending: false })
          .limit(1);

        if (error) {
          console.error('Error fetching removal notifications:', error);
          setLoading(false);
          return;
        }

        if (notifications && notifications.length > 0) {
          const notif = notifications[0];
          
          // Fetch remover's display name using Edge Function
          try {
            const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
            const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';
            const { data: { session } } = await supabase.auth.getSession();
            
            if (session) {
              const response = await fetch(
                `${supabaseUrl}/functions/v1/get-user-display-names`,
                {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${session.access_token}`,
                    'apikey': supabaseAnonKey,
                  },
                  body: JSON.stringify({ userIds: [notif.removed_by] }),
                }
              );

              if (response.ok) {
                const data = await response.json();
                if (data.success && data.displayNames && data.displayNames[notif.removed_by]) {
                  setNotification({
                    ...notif,
                    remover_display_name: data.displayNames[notif.removed_by],
                  });
                } else {
                  setNotification({
                    ...notif,
                    remover_display_name: null,
                  });
                }
              } else {
                setNotification({
                  ...notif,
                  remover_display_name: null,
                });
              }
            } else {
              setNotification({
                ...notif,
                remover_display_name: null,
              });
            }
          } catch (error) {
            console.error('Error fetching remover display name:', error);
            setNotification({
              ...notif,
              remover_display_name: null,
            });
          }
        }
      } catch (error) {
        console.error('Error checking for removal notifications:', error);
      } finally {
        setLoading(false);
      }
    };

    checkForNotifications();
    
    // Also check periodically (every 30 seconds) in case user is on the app when removed
    const interval = setInterval(checkForNotifications, 30000);
    
    // Check when window gains focus (user returns to app)
    const handleFocus = () => {
      checkForNotifications();
    };
    window.addEventListener('focus', handleFocus);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', handleFocus);
    };
  }, [user?.id]);

  const handleDismiss = async () => {
    if (!notification || !user?.id) return;

    try {
      const { error } = await supabase
        .from('car_removal_notifications')
        .update({ dismissed_at: new Date().toISOString() })
        .eq('id', notification.id)
        .eq('user_id', user.id);

      if (error) {
        console.error('Error dismissing notification:', error);
        return;
      }

      setNotification(null);
    } catch (error) {
      console.error('Error dismissing notification:', error);
    }
  };

  if (loading || !notification) {
    return null;
  }

  const removerName = notification.remover_display_name || 'Someone';
  const carInfo = [
    notification.car_make,
    notification.car_model,
    notification.car_license_plate,
  ]
    .filter(Boolean)
    .join(' ') || 'this car';

  const message = t('carSharing.removedFromCarMessage') || '{removerName} has removed you from collaborating on {carInfo}.';
  const formattedMessage = message
    .replace(/{removerName}/g, removerName)
    .replace(/{carInfo}/g, carInfo);

  return (
    <Dialog open={!!notification} onOpenChange={() => {}}>
      <DialogContent 
        className="sm:max-w-md" 
        style={{ fontFamily: 'Lato' }}
        showCloseButton={false}
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold text-black" style={{ fontFamily: 'Lato', fontWeight: 700 }}>
            {t('carSharing.removedFromCar') || 'Removed from Car'}
          </DialogTitle>
          <DialogDescription className="text-sm text-gray-600 mt-2" style={{ fontFamily: 'Lato' }}>
            {formattedMessage}
          </DialogDescription>
        </DialogHeader>
        <div className="flex justify-end mt-4">
          <Button
            onClick={handleDismiss}
            className="bg-[#E7FE52] text-black hover:opacity-90"
            style={{ fontFamily: 'Lato' }}
          >
            {t('actions.close') || 'Close'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CarRemovalNotification;

